/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { PaymentTerminalV2 } from "@lemmonhq/types";
import { getTerminalProviderConfig } from "./providerConfig";
import { ProviderLogo } from "./ProviderLogo";
import { getTerminalIdentifier, getTerminalStatus, statusBadgeClass } from "./utils";

type TerminalSummaryProps = {
  terminal: PaymentTerminalV2;
  /** Label of the payment integration the terminal is paired through. */
  integrationLabel?: string | null;
};

/** Read-only facts about an existing terminal, shown at the top of the editor. */
export const TerminalSummary = defineComponent<TerminalSummaryProps>({
  name: "TerminalSummary",
  props: ["terminal", "integrationLabel"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const { terminal } = props;
      const config = getTerminalProviderConfig(terminal.provider);
      const status = getTerminalStatus(terminal.status);
      const identifier = getTerminalIdentifier(terminal);

      return (
        <div class="rounded-xl border border-base-300 bg-base-200/40 p-4">
          <div class="flex items-center gap-3">
            <ProviderLogo provider={terminal.provider} />
            <div class="min-w-0 flex-1">
              <div class="font-medium text-base-content">{config.label}</div>
              {props.integrationLabel && (
                <div class="truncate text-sm text-base-content/60">
                  {props.integrationLabel}
                </div>
              )}
            </div>
            <span class={statusBadgeClass[status.tone]}>{t(status.labelKey)}</span>
          </div>

          {identifier && (
            <dl class="mt-4 flex items-center justify-between gap-4 border-t border-base-300 pt-3 text-sm">
              <dt class="text-base-content/60">{t(identifier.labelKey)}</dt>
              <dd class="font-mono text-base-content">{identifier.value}</dd>
            </dl>
          )}

          {config.action === "pair" && (
            <p class="mt-3 text-xs text-base-content/60">
              {t("settings.terminals.repair_hint")}
            </p>
          )}
        </div>
      );
    };
  },
});
